import { useEffect, useRef } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Spec §10 — traps Tab/Shift+Tab inside containerRef while isOpen, focuses the first
 * focusable element on open, restores focus to whatever had it before on close, and
 * calls onClose on Escape. Used by BottomSheet; no focus-trap library per the rebuild plan.
 */
const useFocusTrap = (containerRef, isOpen, onClose) => {
  const previouslyFocused = useRef(null)

  // Keep the latest onClose in a ref rather than the effect's dependency array. BottomSheet
  // (and anything using children, like the sheet content re-rendering on every keystroke)
  // passes a freshly-created onClose on every render; depending on it directly would tear
  // down and re-run this effect on every keystroke, which re-focuses the first focusable
  // element (the close button) each time and yanks focus away from whatever the user was
  // typing into. The effect itself should only run when `isOpen` actually changes.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    if (!isOpen) return undefined

    previouslyFocused.current = document.activeElement

    const getFocusables = () => Array.from(containerRef.current?.querySelectorAll(FOCUSABLE_SELECTOR) ?? [])
    // Prefer an element that explicitly asked for autofocus (e.g. the OTP sheet's hidden
    // input) over blindly grabbing the first focusable element in DOM order - otherwise
    // this always wins the race and steals focus to whatever comes first (typically the
    // sheet's own close button), since this effect runs after that element's own
    // autoFocus effect (child effects fire before parent effects in the same commit).
    const preferred = containerRef.current?.querySelector('[autofocus]')
    ;(preferred ?? getFocusables()[0])?.focus()

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current?.()
        return
      }
      if (e.key !== 'Tab') return

      const items = getFocusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      previouslyFocused.current?.focus?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, containerRef])
}

export default useFocusTrap
